import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from './InfoTip.module.css';

export interface InfoTipProps {
  /** Text shown in the tooltip. Line breaks are preserved. */
  text: string;
  /** Which edge of the icon the tooltip hangs from; use `end` for icons near the right edge. */
  align?: 'start' | 'end';
  /** Optional accessible label for the icon; defaults to a localized "More information". */
  label?: string;
  className?: string;
}

/**
 * Small round "i" icon that reveals a tooltip on hover or keyboard focus.
 * Pure CSS, so it is safe inside `<label>` elements and grid cells.
 */
export const InfoTip: React.FC<InfoTipProps> = ({ text, align = 'start', label, className }) => {
  const { t } = useTranslation('common');
  const tooltipId = React.useId();

  return (
    <span
      className={[styles.wrapper, align === 'end' ? styles.alignEnd : '', className ?? ''].filter(Boolean).join(' ')}
      data-info-tip=""
      // Never let the click reach a surrounding label or clickable row.
      onClick={event => { event.preventDefault(); event.stopPropagation(); }}
    >
      <button
        type="button"
        className={styles.icon}
        aria-label={label ?? t('moreInformation', { defaultValue: 'More information' })}
        aria-describedby={tooltipId}
      >
        <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <circle cx="8" cy="4.9" r="0.95" fill="currentColor" />
          <path d="M6.9 7.2h1.6v4.3H6.9z" fill="currentColor" />
        </svg>
      </button>
      <span role="tooltip" id={tooltipId} className={styles.tooltip}>
        {text}
      </span>
    </span>
  );
};
